import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";

import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";

interface ContainerSize {
  width: number;
  height: number;
}

interface ContainerSizeContextType {
  size: ContainerSize;
}

const ContainerSizeContext = createContext<ContainerSizeContextType>({
  size: { width: 0, height: 0 }
});

interface ContainerSizeProviderProps {
  sx?: SxProps;
  children: ReactNode;
}

export const ContainerSizeProvider = ({
  sx = {},
  children
}: ContainerSizeProviderProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      const { width, height } = containerRef.current!.getBoundingClientRect();
      setSize({ width, height });
    };

    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  return (
    <ContainerSizeContext.Provider value={{ size }}>
      <Box ref={containerRef} sx={sx}>
        {children}
      </Box>
    </ContainerSizeContext.Provider>
  );
};

export const useContainerSize = (): ContainerSize => {
  const containerSizeContext = useContext(ContainerSizeContext);
  if (!containerSizeContext) {
    throw new Error(
      "useContainerSize must be used within a ContainerSizeProvider"
    );
  }
  return containerSizeContext.size;
};
